import type { WizardData, WizardField, WizardFieldError, WizardSchema, WizardStep } from '@hive/api';
import { useI18n, type CommonKey } from '@hive/i18n';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ErrorBox, explainProblem, type Explained } from '../ErrorBox';
import { Icon } from '../icons';
import { Meter, cx } from '../layout';
import { useConfirm, useToast } from '../overlay';
import { useOptionalHelp } from '../help';
import { FieldRenderer, type CustomFields, type OptionsByType } from './FieldRenderer';
import { assertStepLimit, fieldOptions, forDraft, initialData, isSecret, lines, mergeDraft, validateLocal, visibleFields } from './schema';

export interface WizardDraftStore {
  load(): Promise<{ step: number; data: WizardData; updatedAt?: string } | null>;
  save(step: number, data: WizardData): Promise<void>;
  discard(): Promise<void>;
}

export interface WizardStepInfo {
  wizard: string;
  stepId: string;
  n: number;
  total: number;
  title: string;
}

export interface WizardProps<R> {
  schema: WizardSchema;
  /** Dados já existentes (edição) ou sugeridos (padrões detectados). */
  initial?: WizardData;
  dynamicOptions?: OptionsByType;
  groupLabel?: (g: string) => string;
  /** Campos desenhados pela própria tela (tipo do campo → componente). */
  customFields?: CustomFields;
  /** Validação do servidor do passo (já traduzida). Sem ela, vale só a validação local. */
  validate?: (stepId: string, data: WizardData) => Promise<Record<string, WizardFieldError>>;
  onSubmit: (data: WizardData) => Promise<R>;
  /** Tela final depois de salvar (ex.: token mostrado uma única vez). */
  renderDone?: (result: R, data: WizardData) => ReactNode;
  /** Conteúdo do passo de teste (`test: true`). */
  renderTest?: (data: WizardData) => ReactNode;
  /** Conteúdo extra no fim de um passo (avisos, prévias). */
  renderStepExtra?: (step: WizardStep, data: WizardData) => ReactNode;
  draft?: WizardDraftStore;
  onCancel?: () => void;
  onStepChange?: (info: WizardStepInfo) => void;
  /** A cada mudança (sem segredos): guarda local para não perder nada se a sessão cair (HU-001). */
  onDataChange?: (step: number, data: WizardData) => void;
  /** Em edição, segredos em branco mantêm o valor do cofre. */
  secretKeep?: boolean;
  submitLabel?: string;
}

/** "Usar modelo" da galeria: os valores do modelo esperam o assistente abrir (uma vez, nesta aba). */
export function setPrefill(wizard: string, data: WizardData) {
  try {
    sessionStorage.setItem('hive.wizard.prefill', JSON.stringify({ wizard, data, at: Date.now() }));
  } catch {
    /* sem armazenamento: o assistente abre vazio */
  }
}

function takePrefill(wizard: string | undefined): WizardData | undefined {
  try {
    const raw = sessionStorage.getItem('hive.wizard.prefill');
    if (!raw || !wizard) return undefined;
    const p = JSON.parse(raw) as { wizard: string; data: WizardData; at: number };
    if (p.wizard !== wizard || Date.now() - p.at > 10 * 60 * 1000) return undefined;
    sessionStorage.removeItem('hive.wizard.prefill');
    return p.data;
  } catch {
    return undefined;
  }
}

function hhmm(d = new Date()) {
  return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
}

/**
 * Motor de assistentes (HU-015, RF-NOC-01/03): passos numerados com barra de progresso,
 * voltar sem perder dados, rascunho, validação na hora com "o que houve / por quê / como resolver",
 * passo de revisão gerado do próprio esquema e foco levado ao título de cada passo.
 */
export function Wizard<R>(p: WizardProps<R>) {
  const { t, l, fmt, ...i18n } = useI18n<CommonKey>();
  const toast = useToast();
  const help = useOptionalHelp();
  const [confirm, confirmNode] = useConfirm();
  assertStepLimit(p.schema);
  const steps = p.schema.steps;
  const [step, setStep] = useState(0);
  const [data, setData] = useState<WizardData>(() => initialData(p.schema, p.initial ?? takePrefill(p.schema.id)));
  const [errors, setErrors] = useState<Record<string, WizardFieldError>>({});
  const [stepError, setStepError] = useState<Explained | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ result: R } | null>(null);
  const [draftAt, setDraftAt] = useState<string | null>(null);
  const [restored, setRestored] = useState<string | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const firstRender = useRef(true);
  const cur = steps[step]!;
  const last = step === steps.length - 1;

  const allFields = useMemo(() => steps.flatMap((s) => s.fields ?? []), [steps]);
  const labelOf = useCallback(
    (id: string) => {
      const f = allFields.find((x) => x.id === id);
      return f ? l(f.label) || f.id : id;
    },
    [allFields, l],
  );

  // Rascunho guardado antes: retoma de onde a pessoa parou.
  const { draft } = p;
  useEffect(() => {
    if (!draft || p.initial) return;
    let alive = true;
    draft
      .load()
      .then((d) => {
        if (!alive || !d) return;
        setData((cur) => mergeDraft(cur, d.data));
        setStep(Math.min(Math.max(0, d.step), steps.length - 1));
        setRestored(d.updatedAt ? fmt.relative(d.updatedAt) : '');
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só ao abrir
  }, []);

  // Foco no título do passo a cada troca (leitores de tela anunciam o passo novo).
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
    } else {
      heading.current?.focus({ preventScroll: false });
    }
    const info = { wizard: p.schema.id, stepId: cur.id, n: step + 1, total: steps.length, title: l(cur.title) };
    p.onStepChange?.(info);
    // A ajuda lateral mostra a explicação do passo atual (HU-018).
    help?.setWizard(done ? null : { id: info.wizard, stepId: info.stepId, n: info.n, total: info.total, title: info.title });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avisa só quando o passo muda
  }, [step, done]);

  const { onDataChange } = p;
  // Só guarda depois que a pessoa mexeu em algo (abrir o assistente não cria rascunho).
  const touched = useRef(false);
  useEffect(() => {
    if (!done && touched.current) onDataChange?.(step, forDraft(p.schema, data));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avisa quando dados ou passo mudam
  }, [data, step, done]);

  // Saiu do assistente: a ajuda volta a ser a da tela.
  useEffect(() => () => help?.setWizard(null), [help?.setWizard]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (id: string, value: unknown) => {
    touched.current = true;
    setData((d) => ({ ...d, [id]: value }));
    if (errors[id]) setErrors(({ [id]: _drop, ...rest }) => rest);
  };

  const explainFieldErrors = (errs: Record<string, WizardFieldError>): Explained => {
    const entries = Object.entries(errs);
    const [fid, first] = entries[0]!;
    return {
      what: t('wz.invalid', { count: entries.length }),
      why: `${labelOf(fid)}: ${first.what ?? ''}`,
      fix: first.fix ?? t('wz.invalidFix'),
      fields: entries.length > 1 ? entries.map(([f, e]) => ({ field: f, label: labelOf(f), message: e.what ?? '' })) : undefined,
    };
  };

  async function checkStep(s: WizardStep): Promise<Record<string, WizardFieldError>> {
    const local = validateLocal(s, data, t, l);
    if (Object.keys(local).length || !p.validate || !(s.fields ?? []).length) return local;
    return p.validate(s.id, data);
  }

  const goTo = (i: number) => {
    setErrors({});
    setStepError(null);
    setStep(i);
  };

  async function next() {
    if (busy) return;
    setBusy(true);
    try {
      const errs = await checkStep(cur);
      if (Object.keys(errs).length) {
        setErrors(errs);
        setStepError(explainFieldErrors(errs));
        heading.current?.focus();
        return;
      }
      if (!last) {
        goTo(step + 1);
        return;
      }
      const result = await p.onSubmit(normalize(p.schema, data));
      setDone({ result });
      setRestored(null);
    } catch (e) {
      const ex = explainProblem(e, { t, lang: i18n.lang, has: i18n.has }, Object.fromEntries(allFields.map((f) => [f.id, l(f.label) || f.id])));
      // Erros de campo voltam para o passo do primeiro campo com problema.
      const fieldErrs = ex.fields ?? [];
      if (fieldErrs.length) {
        const map = Object.fromEntries(fieldErrs.map((f) => [f.field, { what: f.message }]));
        const target = steps.findIndex((s) => (s.fields ?? []).some((f) => f.id in map));
        if (target >= 0 && target !== step) setStep(target);
        setErrors(map);
      }
      setStepError(ex);
      heading.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  async function saveDraft(silent?: boolean) {
    if (!p.draft) return;
    try {
      await p.draft.save(step, forDraft(p.schema, data));
      setDraftAt(hhmm());
      if (!silent) toast(t('wz.draftSavedToast'), { kind: 'ok' });
    } catch (e) {
      const ex = explainProblem(e, { t, lang: i18n.lang, has: i18n.has });
      if (!silent) toast(<>{ex.what}</>, { kind: 'crit' });
    }
  }

  async function cancel() {
    if (!p.onCancel) return;
    if (p.draft && !done) {
      const ok = await confirm({ title: t('wz.cancelTitle'), text: t('wz.cancelText'), ok: t('wz.cancelOk') });
      if (!ok) return;
      await saveDraft(true);
    }
    p.onCancel();
  }

  async function startOver() {
    await p.draft?.discard().catch(() => undefined);
    setData(initialData(p.schema, p.initial));
    setRestored(null);
    setDraftAt(null);
    goTo(0);
  }

  const headingId = `wz-${p.schema.id}-h`;

  if (done) {
    return (
      <div className="wz">
        <section className="wz-body" aria-labelledby={headingId}>
          <h2 id={headingId} ref={heading} tabIndex={-1} className="sr-only">
            {l(p.schema.title)}
          </h2>
          {p.renderDone ? p.renderDone(done.result, data) : null}
        </section>
      </div>
    );
  }

  const pct = Math.round((step / steps.length) * 100);
  return (
    <div className="wz">
      <div className="wz-head">
        <ol className="wz-steps" aria-label={t('wz.steps')}>
          {steps.map((s, i) => (
            <li key={s.id} className={cx('wz-step', i < step && 'done', i === step && 'cur')}>
              <button
                type="button"
                onClick={i < step ? () => goTo(i) : undefined}
                tabIndex={i < step ? 0 : -1}
                aria-disabled={i < step ? undefined : true}
                aria-current={i === step ? 'step' : undefined}
                aria-label={t('wz.stepOf', { n: i + 1, total: steps.length, title: l(s.title) })}
              >
                <span className="bar" />
                <span className="lbl">
                  <span className="n">{i + 1}</span>
                  <span className="t">{l(s.title)}</span>
                </span>
              </button>
            </li>
          ))}
        </ol>
        <div className="wz-mobile">
          <div className="row between">
            <b>{t('wz.stepOf', { n: step + 1, total: steps.length, title: l(cur.title) })}</b>
            <span className="muted small">{pct}%</span>
          </div>
          <Meter value={step + 1} max={steps.length} label={t('wz.stepOf', { n: step + 1, total: steps.length, title: l(cur.title) })} />
        </div>
      </div>
      {restored != null && (
        <div className="wz-banner">
          <Icon name="info" />
          <div className="grow" style={{ flex: 1 }}>
            {t('wz.draftRestored', { when: restored })}
          </div>
          <button type="button" className="btn btn-sm" onClick={startOver}>
            {t('wz.startOver')}
          </button>
        </div>
      )}
      <section className="wz-body" aria-labelledby={headingId}>
        <h2 id={headingId} ref={heading} tabIndex={-1}>
          {step + 1}. {l(cur.title)}
        </h2>
        {cur.lead && <p className="lead">{l(cur.lead)}</p>}
        {stepError && <ErrorBox e={stepError} />}
        {cur.review ? (
          <div className="stack">
            <Review schema={p.schema} data={data} upTo={step} onEdit={goTo} dynamicOptions={p.dynamicOptions} />
            {/* campos do próprio passo de revisão (ex.: "salvar como perfil") */}
            {visibleFields(cur, data).map((f) => (
              <FieldRenderer key={f.id} field={f} data={data} error={errors[f.id]} onChange={setField} dynamicOptions={p.dynamicOptions} secretKeep={p.secretKeep} groupLabel={p.groupLabel} customFields={p.customFields} />
            ))}
          </div>
        ) : (
          <div className="stack">
            {visibleFields(cur, data).map((f) => (
              <FieldRenderer
                key={f.id}
                field={f}
                data={data}
                error={errors[f.id]}
                onChange={setField}
                dynamicOptions={p.dynamicOptions}
                secretKeep={p.secretKeep}
                groupLabel={p.groupLabel}
                customFields={p.customFields}
              />
            ))}
            {cur.test && p.renderTest?.(data)}
          </div>
        )}
        {p.renderStepExtra?.(cur, data)}
      </section>
      <div className="wz-foot">
        {p.onCancel && (
          <button type="button" className="btn btn-ghost" onClick={cancel}>
            {t('ui.cancel')}
          </button>
        )}
        {p.draft && (
          <button type="button" className="btn" onClick={() => saveDraft()}>
            {t('wz.saveDraft')}
          </button>
        )}
        <span className="wz-saved" aria-live="polite">
          {draftAt ? t('wz.draftSavedAt', { time: draftAt }) : busy ? t('wz.checking') : ''}
        </span>
        <span className="spacer" />
        {step > 0 && (
          <button type="button" className="btn" onClick={() => goTo(step - 1)}>
            <Icon name="arrowL" size="sm" />
            {t('ui.back')}
          </button>
        )}
        <button type="button" className="btn btn-primary" onClick={next} disabled={busy} aria-busy={busy || undefined}>
          {last ? p.submitLabel ?? (l(p.schema.submit?.label) || t('wz.finish')) : t('ui.next')}
          {!last && <Icon name="arrowR" size="sm" />}
        </button>
      </div>
      {confirmNode}
    </div>
  );
}

/** Ajusta tipos antes de enviar (listas de e-mail/IPs como texto, números como número). */
function normalize(schema: WizardSchema, data: WizardData): WizardData {
  const out: WizardData = { ...data };
  for (const s of schema.steps)
    for (const f of s.fields ?? []) {
      const v = out[f.id];
      if (v === '' || v == null) {
        if (!f.required && f.type !== 'boolean') delete out[f.id];
        continue;
      }
      if (f.type === 'number') out[f.id] = Number(v);
      if (f.type === 'email-list' || f.type === 'cidr-list') out[f.id] = lines(v).join('\n');
    }
  return out;
}

/** Passo de revisão: o que foi preenchido em cada passo, com "Editar" que volta ao passo. */
function Review({
  schema,
  data,
  upTo,
  onEdit,
  dynamicOptions,
}: {
  schema: WizardSchema;
  data: WizardData;
  upTo: number;
  onEdit: (i: number) => void;
  dynamicOptions?: OptionsByType;
}) {
  const { t, l } = useI18n<CommonKey>();
  const show = (f: WizardField): ReactNode => {
    const v = data[f.id];
    if (isSecret(f)) return v ? t('wz.secretHidden') : t('ui.none');
    if (v == null || v === '' || (Array.isArray(v) && !v.length)) return t('ui.none');
    if (f.type === 'boolean') return v === true || v === 'true' ? t('ui.yes') : t('ui.no');
    if (f.type === 'select') return fieldOptions(f, l).find((o) => o.value === String(v))?.label ?? String(v);
    const list = Array.isArray(v) ? v.map(String) : lines(v);
    if (f.type === 'permission-matrix') return t('wz.permCount', { count: list.length });
    if (f.type === 'multiselect') return list.map((x) => fieldOptions(f, l).find((o) => o.value === x)?.label ?? x).join(', ');
    if (dynamicOptions?.[f.type]) return list.map((x) => dynamicOptions[f.type]?.find((o) => o.value === x)?.label ?? x).join(', ');
    if (f.type === 'email-list' || f.type === 'cidr-list' || f.type === 'tags') return list.join(', ');
    return String(v);
  };
  return (
    <>
      <p className="lead">{t('wz.reviewLead')}</p>
      <dl className="summary">
        {schema.steps.slice(0, upTo).flatMap((s, i) =>
          visibleFields(s, data).map((f) => (
            <div key={f.id} style={{ display: 'contents' }}>
              <dt>{l(f.label) || f.id}</dt>
              <dd style={{ overflowWrap: 'anywhere' }}>{show(f)}</dd>
              <dd className="ed">
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => onEdit(i)} aria-label={`${t('ui.edit')}: ${l(f.label) || f.id}`}>
                  {t('ui.edit')}
                </button>
              </dd>
            </div>
          )),
        )}
      </dl>
    </>
  );
}
